import { useEffect, useMemo, useRef } from 'react';
import uPlot from 'uplot';
import type { LapRef, ResampledLap } from '@tla/types';
import { deltaTime, toKmh } from '@tla/core';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { lapColour, lapKey } from '../../store/colours';
import { lapDescriptor } from '../../util/lapLabel';
import { UplotChart } from './UplotChart';
import { GraphToolbar } from './GraphToolbar';
import { PlaybackBar } from './PlaybackBar';
import { LapEventsTimeline } from './LapEventsTimeline';

/** Channels displayed as km/h rather than raw m/s. */
const SPEED_CHANNELS = new Set(['speed_gps', 'speed_obd', 'speed_calc']);

/** Uniform height (px) for the delta plot and every tiled channel plot. */
const GRAPH_HEIGHT = 200;

export function ChannelGraphs({ showPlayback = true }: { showPlayback?: boolean } = {}): React.ReactElement {
  const referenceLap = useWorkspaceStore((s) => s.referenceLap);
  const comparisonLaps = useWorkspaceStore((s) => s.comparisonLaps);
  const resampled = useWorkspaceStore((s) => s.resampled);
  const channelConfig = useWorkspaceStore((s) => s.channelConfig);
  const channelMeta = useWorkspaceStore((s) => s.channelMeta);
  const graphMode = useWorkspaceStore((s) => s.graphMode);
  const graphGrid = useWorkspaceStore((s) => s.graphGrid);
  const zoom = useWorkspaceStore((s) => s.zoom);
  const setZoom = useWorkspaceStore((s) => s.setZoom);
  const hiddenLaps = useWorkspaceStore((s) => s.hiddenLaps);
  const sessions = useWorkspaceStore((s) => s.sessions);
  const drivers = useWorkspaceStore((s) => s.drivers);
  const summaries = useWorkspaceStore((s) => s.summaries);

  const laps: LapRef[] = useMemo(
    () => (referenceLap ? [referenceLap, ...comparisonLaps] : []),
    [referenceLap, comparisonLaps],
  );

  const orderedLaps = useMemo(
    () =>
      laps
        .map((ref, i) => ({ ref, colour: lapColour(i), lap: resampled[lapKey(ref)] }))
        .filter((x): x is { ref: LapRef; colour: string; lap: ResampledLap } => Boolean(x.lap)),
    [laps, resampled],
  );

  /** Rich legend labels ("JJ · S1 · L7 · 1:15.225") keyed by lapKey. */
  const lapLabels = useMemo(() => {
    const m: Record<string, string> = {};
    for (const o of orderedLaps) m[lapKey(o.ref)] = lapDescriptor(o.ref, sessions, drivers, summaries);
    return m;
  }, [orderedLaps, sessions, drivers, summaries]);

  /** Laps actually drawn: reference (baseline) is kept, hidden laps dropped. */
  const visibleLaps = useMemo(
    () => orderedLaps.filter((o) => !hiddenLaps.includes(lapKey(o.ref))),
    [orderedLaps, hiddenLaps],
  );

  const reference = orderedLaps[0]?.lap;

  // Shared x-axis = reference lap distance (metres). All laps share the grid size.
  const xDist = reference?.distance;

  const visibleChannels = useMemo(
    () =>
      [...channelConfig]
        .filter((c) => c.visible)
        .sort((a, b) => a.order - b.order)
        .map((c) => c.channelId),
    [channelConfig],
  );

  // Delta-t plot data: target.time - reference.time per comparison lap.
  const deltaData = useMemo<uPlot.AlignedData | null>(() => {
    if (!reference || !xDist) return null;
    const series: number[][] = visibleLaps.map((o) =>
      o.lap === reference ? Array.from({ length: xDist.length }, () => 0) : Array.from(deltaTime(reference, o.lap)),
    );
    return [Array.from(xDist), ...series] as unknown as uPlot.AlignedData;
  }, [reference, xDist, visibleLaps]);

  const deltaSeries = useMemo<uPlot.Series[]>(
    () => [
      { label: 'Distance' },
      ...visibleLaps.map((o) => ({
        label: lapLabels[lapKey(o.ref)] ?? `#${o.ref.lapNumber}`,
        stroke: o.colour,
        width: o.ref === referenceLap ? 1 : 1.5,
        value: (_u: uPlot, v: number) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(3)}s`),
      })),
    ],
    [visibleLaps, referenceLap, lapLabels],
  );

  const colourList = visibleLaps.map((o) => o.colour);

  // --- Mobile pinch-zoom on the x (distance) axis ------------------------
  // Two-finger pinch on the graph stack zooms the shared distance range (which
  // every chart + the map follow via `zoom`), and a two-finger drag pans it.
  // Single-finger touches are left alone so native list scroll and the uPlot
  // tap cursor keep working. Kept on the x axis so uPlot re-renders sharply
  // rather than a blurry visual scale.
  const graphsRef = useRef<HTMLDivElement>(null);
  const maxDist = xDist ? xDist[xDist.length - 1] : 0;
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  useEffect(() => {
    const el = graphsRef.current;
    if (!el || !maxDist) return;
    if (!window.matchMedia('(max-width: 820px)').matches) return;

    let startSpread = 0;
    let startRange: [number, number] = [0, maxDist];
    let startMidFrac = 0.5;
    let startMidX = 0;

    const onTouchStart = (e: TouchEvent): void => {
      if (e.touches.length !== 2) return;
      const rect = el.getBoundingClientRect();
      startSpread = Math.abs(e.touches[0].clientX - e.touches[1].clientX);
      startRange = zoomRef.current ?? [0, maxDist];
      startMidX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      startMidFrac = Math.min(1, Math.max(0, (startMidX - rect.left) / rect.width));
    };
    const onTouchMove = (e: TouchEvent): void => {
      if (e.touches.length !== 2 || startSpread <= 0) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const spread = Math.abs(e.touches[0].clientX - e.touches[1].clientX) || 1;
      const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const [d0, d1] = startRange;
      const span0 = d1 - d0;
      const focal = d0 + startMidFrac * span0;
      // Pinch out (spread grows) → smaller span (zoom in). Clamp to a sane
      // minimum window and the full lap.
      let span = Math.min(maxDist, Math.max(20, (span0 * startSpread) / spread));
      // Two-finger drag pans the window in distance units.
      const panDist = -((midX - startMidX) / rect.width) * span;
      let nd0 = focal - startMidFrac * span + panDist;
      let nd1 = nd0 + span;
      if (nd0 < 0) {
        nd0 = 0;
        nd1 = span;
      }
      if (nd1 > maxDist) {
        nd1 = maxDist;
        nd0 = Math.max(0, maxDist - span);
      }
      if (span >= maxDist) setZoom(null);
      else setZoom([nd0, nd1]);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
    };
  }, [maxDist, setZoom]);

  if (!reference || !xDist) {
    return (
      <div className="col-centre">
        <GraphToolbar />
        <div className="graphs" style={{ display: 'grid', placeItems: 'center' }}>
          <div className="muted">Select a lap from a session to begin.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-centre">
      <GraphToolbar />
      {showPlayback && <PlaybackBar />}
      <LapEventsTimeline laps={visibleLaps} labels={lapLabels} />
      <div className="graphs" ref={graphsRef}>
        {deltaData && (
          <UplotChart
            title="Time delta vs reference (s)"
            data={deltaData}
            series={deltaSeries}
            height={GRAPH_HEIGHT}
            xRange={zoom}
            onZoom={setZoom}
          />
        )}

        {graphMode === 'tiled' && (
          <div className={`tiled-graphs${graphGrid ? ' grid' : ''}`}>
            {visibleChannels.map((chId) => (
              <ChannelPlot
                key={chId}
                channelId={chId}
                name={channelMeta[chId]?.name ?? chId}
                unit={channelMeta[chId]?.unit ?? ''}
                xDist={xDist}
                orderedLaps={visibleLaps}
                colours={colourList}
                labels={lapLabels}
                zoom={zoom}
                onZoom={setZoom}
              />
            ))}
          </div>
        )}

        {graphMode === 'overlapped' && (
          <OverlappedPlot
            channelIds={visibleChannels}
            names={channelMeta}
            xDist={xDist}
            orderedLaps={visibleLaps}
            zoom={zoom}
            onZoom={setZoom}
          />
        )}
      </div>
    </div>
  );
}

/** Convert a channel's values for display (speed → km/h). */
function displayValues(channelId: string, values: Float64Array | undefined): number[] {
  if (!values) return [];
  if (SPEED_CHANNELS.has(channelId)) return Array.from(values, (v) => toKmh(v));
  return Array.from(values);
}

function unitLabel(channelId: string, unit: string): string {
  return SPEED_CHANNELS.has(channelId) ? 'km/h' : unit;
}

function ChannelPlot(props: {
  channelId: string;
  name: string;
  unit: string;
  xDist: Float64Array;
  orderedLaps: { ref: LapRef; colour: string; lap: ResampledLap }[];
  colours: string[];
  labels: Record<string, string>;
  zoom: [number, number] | null;
  onZoom: (range: [number, number] | null) => void;
}): React.ReactElement {
  const { channelId, xDist, orderedLaps } = props;
  const unit = unitLabel(channelId, props.unit);

  const data = useMemo<uPlot.AlignedData>(() => {
    const series = orderedLaps.map((o) => displayValues(channelId, o.lap.channels[channelId]));
    return [Array.from(xDist), ...series] as unknown as uPlot.AlignedData;
  }, [channelId, xDist, orderedLaps]);

  const series = useMemo<uPlot.Series[]>(
    () => [
      { label: 'Distance' },
      ...orderedLaps.map((o) => ({
        label: props.labels[lapKey(o.ref)] ?? `#${o.ref.lapNumber}`,
        stroke: o.colour,
        width: 1.25,
        value: (_u: uPlot, v: number) => (v == null ? '—' : v.toFixed(2)),
      })),
    ],
    [orderedLaps, props.labels],
  );

  return (
    <UplotChart
      title={`${props.name} (${unit})`}
      data={data}
      series={series}
      height={GRAPH_HEIGHT}
      xRange={props.zoom}
      onZoom={props.onZoom}
    />
  );
}

function OverlappedPlot(props: {
  channelIds: string[];
  names: Record<string, { name: string; unit: string; group: string }>;
  xDist: Float64Array;
  orderedLaps: { ref: LapRef; colour: string; lap: ResampledLap }[];
  zoom: [number, number] | null;
  onZoom: (range: [number, number] | null) => void;
}): React.ReactElement {
  const { channelIds, xDist, orderedLaps } = props;

  // RPM has a very different scale and dominates the overlay, so it is excluded
  // from the overlapped view by default.
  const overlapChannels = useMemo(() => channelIds.filter((id) => id !== 'rpm'), [channelIds]);

  // In overlapped mode we plot the reference lap only, all channels together.
  const ref = orderedLaps[0]?.lap;
  const data = useMemo<uPlot.AlignedData>(() => {
    const series = overlapChannels.map((id) => displayValues(id, ref?.channels[id]));
    return [Array.from(xDist), ...series] as unknown as uPlot.AlignedData;
  }, [overlapChannels, xDist, ref]);

  const palette = ['#4aa3ff', '#ff5c5c', '#4ade80', '#fbbf24', '#c084fc', '#22d3ee', '#fb923c'];
  const series = useMemo<uPlot.Series[]>(
    () => [
      { label: 'Distance' },
      ...overlapChannels.map((id, i) => ({
        label: props.names[id]?.name ?? id,
        stroke: palette[i % palette.length],
        width: 1.25,
      })),
    ],
    [overlapChannels, props.names],
  );

  return <UplotChart title="Overlapped (reference lap)" data={data} series={series} height={360} xRange={props.zoom} onZoom={props.onZoom} />;
}
